import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { socketService } from '../services/socket';
import { useSnackbar } from 'notistack';
import Grid from '@mui/material/Grid2';
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  useTheme,
  alpha,
  Stack,
  Badge,
  ToggleButtonGroup,
  ToggleButton,
  Avatar,
  AvatarGroup,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Switch,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CalendarMonth,
  AccessTime,
  VideocamOutlined,
  PhoneOutlined,
  PersonOutline,
  Person,
  LocationOn,
  Add,
  Remove,
  Send,
  Close,
  Event,
  Schedule,
  Language,
  Link as LinkIcon,
  ContentCopy,
  CheckCircle,
  Cancel,
  Info,
  Groups,
  Videocam,
  Phone,
  Computer,
  NavigateBefore,
  NavigateNext,
  Today,
  WbSunny,
  NightsStay,
  LightMode,
  DarkMode,
  Brightness4,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/ui/PageHeader';
import { GlassCard } from '../components/ui/GlassCard';
import UserSelector from '../components/UserSelector';
import { AgentUser } from '../types';
import { format, addDays, startOfWeek, addHours, setHours, setMinutes, isSameDay, isAfter, isBefore, parseISO, startOfDay, addWeeks, isPast } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';

interface TimeSlot {
  start: Date;
  end: Date;
  available: boolean;
  tentative?: boolean;
}

interface TimeZoneDisplay {
  id: string;
  label: string;
  offset: string;
  current: string;
}

interface Attendee {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  status?: 'accepted' | 'declined' | 'tentative' | 'pending';
}

const meetingTypes = [
  { value: 'video', label: 'Video Call', icon: <Videocam /> },
  { value: 'phone', label: 'Phone Call', icon: <Phone /> },
  { value: 'in-person', label: 'In Person', icon: <PersonOutline /> },
  { value: 'screen-share', label: 'Screen Share', icon: <Computer /> },
];

const platforms = [
  { value: 'zoom', label: 'Zoom', color: '#2D8CFF' },
  { value: 'teams', label: 'Microsoft Teams', color: '#6264A7' },
  { value: 'meet', label: 'Google Meet', color: '#00AC47' },
  { value: 'custom', label: 'Custom Link', color: '#6366F1' },
];

const commonTimezones = [
  { value: 'America/New_York', label: 'Eastern Time (ET)', offset: -5 },
  { value: 'America/Chicago', label: 'Central Time (CT)', offset: -6 },
  { value: 'America/Denver', label: 'Mountain Time (MT)', offset: -7 },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', offset: -8 },
  { value: 'Europe/London', label: 'London (GMT)', offset: 0 },
  { value: 'Europe/Paris', label: 'Paris (CET)', offset: 1 },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: 9 },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)', offset: 11 },
];

const Meetings: React.FC = () => {
  const theme = useTheme();
  const user = useSelector((state: RootState) => state.auth.user);
  const { enqueueSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlots, setSelectedTimeSlots] = useState<Date[]>([]);
  const [linkCopied, setLinkCopied] = useState(false);
  const [viewMode, setViewMode] = useState<'day' | 'week'>('week');
  const [selectedCustomer, setSelectedCustomer] = useState<AgentUser | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarDate, setCalendarDate] = useState<Date | null>(new Date());
  
  // Generate mock meeting dates once to prevent regeneration on every render
  const [mockMeetingDates] = useState(() => {
    const dates = new Set<string>();
    const today = new Date();
    // Add some random dates with meetings in the current month
    for (let i = 0; i < 8; i++) {
      const randomDay = Math.floor(Math.random() * 28) + 1;
      const mockDate = new Date(today.getFullYear(), today.getMonth(), randomDay);
      dates.add(format(mockDate, 'yyyy-MM-dd'));
    }
    return dates;
  });
  
  const [formData, setFormData] = useState({
    title: '',
    duration: 60,
    type: 'video',
    platform: 'zoom',
    customLink: '',
    includeDialIn: false,
    yourTimezone: 'America/New_York',
    customerTimezone: 'America/Los_Angeles',
    additionalTimezones: [] as string[],
    recipients: [] as string[],
    sendCalendarInvite: true,
    customMessage: '',
  });
  const [newRecipient, setNewRecipient] = useState('');

  // Generate time slots for demo (9 AM to 5 PM)
  const generateTimeSlots = (): TimeSlot[] => {
    const baseDate = new Date();
    const slots: TimeSlot[] = [];
    const hours = [9, 10, 11, 14, 15, 16]; // Sample hours with lunch break
    
    hours.forEach((hour, index) => {
      slots.push({
        start: setHours(setMinutes(baseDate, 0), hour),
        end: setHours(setMinutes(baseDate, 0), hour + 1),
        available: hour !== 11, // 11 AM is busy
        tentative: hour === 15, // 3 PM is tentative
      });
    });
    
    return slots;
  };
  
  const [availableSlots] = useState<TimeSlot[]>(generateTimeSlots());

  const generateMeetingLink = () => {
    if (formData.platform === 'custom') return formData.customLink;
    const id = Math.random().toString(36).substr(2, 9);
    switch (formData.platform) {
      case 'zoom':
        return `https://zoom.us/j/${id}`;
      case 'teams':
        return `https://teams.microsoft.com/l/meetup-join/${id}`;
      case 'meet':
        return `https://meet.google.com/${id}`;
      default:
        return '';
    }
  };

  const copyLink = () => {
    const link = generateMeetingLink();
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
    enqueueSnackbar('Meeting link copied to clipboard', { variant: 'success' });
  };

  const handleSendInvitation = async () => {
    if (!formData.title || selectedTimeSlots.length === 0 || formData.recipients.length === 0) {
      enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
      return;
    }

    setSending(true);
    try {
      const socket = socketService.getSocket();
      if (!socket) {
        enqueueSnackbar('Socket not connected', { variant: 'error' });
        setSending(false);
        return;
      }

      // Use recipients array directly
      const recipientEmails = formData.recipients;
      
      // For each proposed time slot, create a meeting request
      for (const slot of selectedTimeSlots) {
        const startTime = slot.toISOString();
        const endTime = addHours(slot, formData.duration / 60).toISOString();

        socket.emit('createMeetingRequest', {
          subject: formData.title,
          attendees: recipientEmails.map(email => ({ email, name: email.split('@')[0] })),
          startTime,
          endTime,
          timeZone: formData.yourTimezone,
          location: formData.type === 'video' ? generateMeetingLink() : '',
          isRecurring: false,
          customMessage: formData.customMessage,
          includeCalendarInvite: formData.sendCalendarInvite,
        }, (response: any) => {
          if (response.success) {
            console.log('Meeting created:', response.meetingId);
          } else {
            console.error('Failed to create meeting:', response.error);
          }
        });
      }

      enqueueSnackbar('Meeting invitations sent successfully', { variant: 'success' });
      
      // Reset form
      setFormData({
        title: '',
        duration: 60,
        type: 'video',
        platform: 'zoom',
        customLink: '',
        includeDialIn: false,
        yourTimezone: 'America/New_York',
        customerTimezone: 'America/Los_Angeles',
        additionalTimezones: [],
        recipients: [],
        sendCalendarInvite: true,
        customMessage: '',
      });
      setSelectedTimeSlots([]);
      setSending(false);
    } catch (error) {
      console.error('Failed to send invitations:', error);
      enqueueSnackbar('Failed to send invitations', { variant: 'error' });
      setSending(false);
    }
  };

  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    // Listen for meeting updates
    socket.on('meetingCreated', (data: any) => {
      console.log('Meeting created:', data);
    });

    socket.on('meetingResponseReceived', (data: any) => {
      console.log('Meeting response received:', data);
      enqueueSnackbar(`${data.responderId} ${data.response} the meeting`, { variant: 'info' });
    });

    return () => {
      socket.off('meetingCreated');
      socket.off('meetingResponseReceived');
    };
  }, [enqueueSnackbar]);

  const getTimeInZone = (date: Date, timezone: string) => {
    return formatInTimeZone(date, timezone, 'h:mm a');
  };

  const getSlotColor = (slot: TimeSlot, day: Date) => {
    const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
    
    // Past slots should be grayed out
    if (isPast(slotDateTime)) return alpha(theme.palette.action.disabled, 0.1);
    
    if (!slot.available) return alpha(theme.palette.error.main, 0.1);
    if (slot.tentative) return alpha(theme.palette.warning.main, 0.1);
    if (selectedTimeSlots.some(s => 
      isSameDay(s, day) && s.getHours() === slot.start.getHours()
    )) {
      return alpha(theme.palette.primary.main, 0.2);
    }
    return alpha(theme.palette.success.main, 0.1);
  };

  const getSlotBorder = (slot: TimeSlot, day: Date) => {
    const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
    
    // Past slots should be grayed out
    if (isPast(slotDateTime)) return theme.palette.action.disabled;
    
    if (!slot.available) return theme.palette.error.main;
    if (slot.tentative) return theme.palette.warning.main;
    if (selectedTimeSlots.some(s => 
      isSameDay(s, day) && s.getHours() === slot.start.getHours()
    )) {
      return theme.palette.primary.main;
    }
    return theme.palette.success.main;
  };

  const toggleSlotSelection = (slot: TimeSlot, day: Date) => {
    if (!slot.available) return;
    
    // Create a new date with the day and time combined
    const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
    
    // Don't allow selection of past times
    if (isPast(slotDateTime)) {
      enqueueSnackbar('Cannot book meetings in the past', { variant: 'warning' });
      return;
    }
    
    const isSelected = selectedTimeSlots.some(s => 
      isSameDay(s, day) && s.getHours() === slot.start.getHours()
    );
    
    if (isSelected) {
      setSelectedTimeSlots(prev => 
        prev.filter(s => !(isSameDay(s, day) && s.getHours() === slot.start.getHours()))
      );
    } else {
      setSelectedTimeSlots(prev => [...prev, slotDateTime]);
    }
  };

  const weekDays = useMemo(() => {
    if (viewMode === 'week') {
      // For week view, show 7 days starting from selected date
      const start = startOfDay(selectedDate);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    } else {
      // For day view, just show the selected date
      return [selectedDate];
    }
  }, [selectedDate, viewMode]);

  const getTimeIcon = (hour: number) => {
    if (hour >= 6 && hour < 12) return <WbSunny fontSize="small" />;
    if (hour >= 12 && hour < 17) return <LightMode fontSize="small" />;
    if (hour >= 17 && hour < 20) return <Brightness4 fontSize="small" />;
    return <NightsStay fontSize="small" />;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title="Schedule Meeting"
        subtitle="Find the perfect time across time zones and send meeting invitations"
        breadcrumbs={[
          { label: 'Home', href: '/dashboard' },
          { label: 'Meetings', href: '/meetings' },
          { label: 'Schedule' },
        ]}
        actions={
          <Button
            variant="outlined"
            startIcon={<CalendarMonth />}
            onClick={() => setCalendarOpen(true)}
            sx={{ borderWidth: 2 }}
          >
            View Calendar
          </Button>
        }
      />

      <Grid container spacing={3}>
        {/* Customer and Recipients Selection */}
        <Grid size={12}>
          <Grid container spacing={3}>
            {/* Customer Selection */}
            <Grid size={{ xs: 12, md: 6 }}>
              <GlassCard gradient>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Person sx={{ mr: 2, color: theme.palette.primary.main }} />
                  <Typography variant="h6" fontWeight={600}>
                    Primary Recipient
                  </Typography>
                  {selectedCustomer && (
                    <Chip
                      label={selectedCustomer.isOnline ? 'Online' : 'Offline'}
                      size="small"
                      sx={{ ml: 2 }}
                      color={selectedCustomer.isOnline ? 'success' : 'default'}
                    />
                  )}
                </Box>

                <Box>
                  <UserSelector
                    value={selectedCustomer}
                    onChange={(user) => {
                      const previousCustomer = selectedCustomer;
                      setSelectedCustomer(user);
                      
                      if (user) {
                        // Add new customer to recipients if not already there
                        if (!formData.recipients.includes(user.email)) {
                          setFormData(prev => ({
                            ...prev,
                            recipients: [...prev.recipients, user.email],
                            customerTimezone: prev.customerTimezone,
                          }));
                        }
                      } else if (previousCustomer) {
                        // Remove previous customer from recipients when clearing selection
                        setFormData(prev => ({
                          ...prev,
                          recipients: prev.recipients.filter(email => email !== previousCustomer.email),
                        }));
                      }
                    }}
                    label="Search for Customer"
                    placeholder="Type to search by name, email, or company..."
                    showCompanyFilter={false}
                  />
                </Box>

                <Box sx={{ 
                  mt: 3,
                  p: 2, 
                  borderRadius: 2,
                  minHeight: 140,
                  background: selectedCustomer ? alpha(theme.palette.primary.main, 0.05) : alpha(theme.palette.action.hover, 0.05),
                  border: `1px solid ${selectedCustomer ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.divider, 0.1)}`,
                  display: 'flex',
                  alignItems: selectedCustomer ? 'flex-start' : 'center',
                  justifyContent: selectedCustomer ? 'flex-start' : 'center',
                }}>
                  {selectedCustomer ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Name</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedCustomer.fullName}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedCustomer.email}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Company</Typography>
                        <Typography variant="body2" fontWeight={500}>{selectedCustomer.agentName || 'N/A'}</Typography>
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Select a customer to see their details
                    </Typography>
                  )}
                </Box>
              </GlassCard>
            </Grid>

            {/* Additional Recipients */}
            <Grid size={{ xs: 12, md: 6 }}>
              <GlassCard gradient>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Groups sx={{ mr: 2, color: theme.palette.secondary.main }} />
                  <Typography variant="h6" fontWeight={600}>
                    Meeting Recipients
                  </Typography>
                  <Badge 
                    badgeContent={formData.recipients.length} 
                    color="primary" 
                    sx={{ ml: 2 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Add recipient email"
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newRecipient.trim()) {
                          e.preventDefault();
                          if (!formData.recipients.includes(newRecipient.trim())) {
                            setFormData(prev => ({
                              ...prev,
                              recipients: [...prev.recipients, newRecipient.trim()]
                            }));
                          }
                          setNewRecipient('');
                        }
                      }}
                      placeholder="email@example.com"
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (newRecipient.trim() && !formData.recipients.includes(newRecipient.trim())) {
                          setFormData(prev => ({
                            ...prev,
                            recipients: [...prev.recipients, newRecipient.trim()]
                          }));
                          setNewRecipient('');
                        }
                      }}
                      disabled={!newRecipient.trim()}
                      sx={{ minWidth: 'auto', px: 3 }}
                    >
                      Add
                    </Button>
                  </Box>

                  <Paper
                    variant="outlined"
                    sx={{
                    minHeight: 140,
                    maxHeight: 140,
                    overflowY: 'auto',
                    p: 1,
                    backgroundColor: alpha(theme.palette.background.paper, 0.5),
                    display: 'flex',
                    alignItems: formData.recipients.length === 0 ? 'center' : 'flex-start',
                    justifyContent: formData.recipients.length === 0 ? 'center' : 'flex-start',
                  }}
                >
                  {formData.recipients.length === 0 ? (
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No recipients added yet
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Select a customer or add email addresses
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <AnimatePresence>
                        {formData.recipients.map((email, index) => (
                          <motion.div
                            key={email}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Chip
                              label={email}
                              onDelete={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  recipients: prev.recipients.filter((_, i) => i !== index)
                                }));
                              }}
                              variant="outlined"
                              sx={{
                                width: '100%',
                                justifyContent: 'space-between',
                                '& .MuiChip-label': {
                                  flex: 1,
                                  textAlign: 'left',
                                },
                              }}
                            />
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </Stack>
                  )}
                </Paper>

                {formData.recipients.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {formData.recipients.length} recipient{formData.recipients.length !== 1 ? 's' : ''} will receive the invitation
                  </Typography>
                )}
                </Box>
              </GlassCard>
            </Grid>
          </Grid>
        </Grid>

        {/* Meeting Details */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <GlassCard gradient>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Meeting Details
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  label="Meeting Title"
                  fullWidth
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Project Review Meeting"
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Duration</InputLabel>
                  <Select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: Number(e.target.value) })}
                    label="Duration"
                  >
                    <MenuItem value={15}>15 minutes</MenuItem>
                    <MenuItem value={30}>30 minutes</MenuItem>
                    <MenuItem value={45}>45 minutes</MenuItem>
                    <MenuItem value={60}>1 hour</MenuItem>
                    <MenuItem value={90}>1.5 hours</MenuItem>
                    <MenuItem value={120}>2 hours</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Meeting Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Meeting Type"
                  >
                    {meetingTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth>
                  <InputLabel>Platform</InputLabel>
                  <Select
                    value={formData.platform}
                    onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                    label="Platform"
                  >
                    {platforms.map(platform => (
                      <MenuItem key={platform.value} value={platform.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              backgroundColor: platform.color,
                            }}
                          />
                          {platform.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {formData.platform === 'custom' && (
                <Grid size={12}>
                  <TextField
                    label="Meeting Link"
                    fullWidth
                    value={formData.customLink}
                    onChange={(e) => setFormData({ ...formData, customLink: e.target.value })}
                    placeholder="https://..."
                    InputProps={{
                      endAdornment: (
                        <IconButton onClick={copyLink}>
                          {linkCopied ? <CheckCircle color="success" /> : <ContentCopy />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              )}

              {formData.platform !== 'custom' && (
                <Grid size={12}>
                  <Paper
                    sx={{
                      p: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LinkIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {generateMeetingLink()}
                        </Typography>
                      </Box>
                      <IconButton size="small" onClick={copyLink}>
                        {linkCopied ? <CheckCircle color="success" /> : <ContentCopy />}
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              )}

              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.includeDialIn}
                      onChange={(e) => setFormData({ ...formData, includeDialIn: e.target.checked })}
                    />
                  }
                  label="Include dial-in information"
                />
              </Grid>

            </Grid>
          </GlassCard>
        </Grid>

        {/* Time Zone Configuration */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            <GlassCard>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Language sx={{ mr: 2, color: theme.palette.primary.main }} />
                <Typography variant="h6" fontWeight={600}>
                  Time Zones
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Autocomplete
                  options={commonTimezones}
                  getOptionLabel={(option) => option.label}
                  value={commonTimezones.find(tz => tz.value === formData.yourTimezone)}
                  onChange={(e, value) => value && setFormData({ ...formData, yourTimezone: value.value })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Your Time Zone"
                      size="small"
                    />
                  )}
                />

                <Autocomplete
                  options={commonTimezones}
                  getOptionLabel={(option) => option.label}
                  value={commonTimezones.find(tz => tz.value === formData.customerTimezone)}
                  onChange={(e, value) => value && setFormData({ ...formData, customerTimezone: value.value })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selectedCustomer ? `${selectedCustomer.fullName.split(' ')[0]}'s Time Zone` : "Customer Time Zone"}
                      size="small"
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={commonTimezones.filter(tz => 
                    tz.value !== formData.yourTimezone && 
                    tz.value !== formData.customerTimezone
                  )}
                  getOptionLabel={(option) => option.label}
                  value={formData.additionalTimezones.map(tz => 
                    commonTimezones.find(ctz => ctz.value === tz)
                  ).filter(Boolean) as typeof commonTimezones}
                  onChange={(e, value) => setFormData({ 
                    ...formData, 
                    additionalTimezones: value.map(v => v.value) 
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Additional Time Zones"
                      placeholder="Add more..."
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          variant="outlined"
                          label={option.label}
                          size="small"
                          {...tagProps}
                        />
                      );
                    })
                  }
                />
              </Stack>

              {/* Time Zone Display */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Current Time
                </Typography>
                <Stack spacing={1}>
                  {[formData.yourTimezone, formData.customerTimezone, ...formData.additionalTimezones].map((tz, index) => {
                    const tzInfo = commonTimezones.find(ctz => ctz.value === tz);
                    if (!tzInfo) return null;
                    
                    return (
                      <Box
                        key={tz}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 1,
                          borderRadius: 1,
                          backgroundColor: alpha(
                            index === 0 ? theme.palette.primary.main : 
                            index === 1 ? theme.palette.secondary.main : 
                            theme.palette.info.main, 
                            0.05
                          ),
                        }}
                      >
                        <Typography variant="caption" fontWeight={500}>
                          {tzInfo.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {formatInTimeZone(new Date(), tz, 'h:mm a')}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            </GlassCard>

          </Stack>
        </Grid>

        {/* Time Selection Grid */}
        <Grid size={12}>
          <GlassCard>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>
                Select Available Times
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={(e, value) => value && setViewMode(value)}
                  size="small"
                >
                  <ToggleButton value="day">Day</ToggleButton>
                  <ToggleButton value="week">Week</ToggleButton>
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton 
                    size="small"
                    onClick={() => setSelectedDate(prev => addDays(prev, viewMode === 'week' ? -7 : -1))}
                    title={viewMode === 'week' ? 'Previous week' : 'Previous day'}
                  >
                    <NavigateBefore />
                  </IconButton>
                  <Button
                    startIcon={<Today />}
                    size="small"
                    onClick={() => setSelectedDate(new Date())}
                  >
                    {viewMode === 'week' 
                      ? format(selectedDate, 'MMMM yyyy')
                      : format(selectedDate, 'EEEE, MMMM d, yyyy')
                    }
                  </Button>
                  <IconButton 
                    size="small"
                    onClick={() => setSelectedDate(prev => addDays(prev, viewMode === 'week' ? 7 : 1))}
                    title={viewMode === 'week' ? 'Next week' : 'Next day'}
                  >
                    <NavigateNext />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label="Available"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  color: theme.palette.success.main,
                }}
              />
              <Chip
                size="small"
                label="Tentative"
                sx={{
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                }}
              />
              <Chip
                size="small"
                label="Busy"
                sx={{
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                  color: theme.palette.error.main,
                }}
              />
              <Chip
                size="small"
                label="Past"
                sx={{
                  backgroundColor: alpha(theme.palette.action.disabled, 0.1),
                  color: theme.palette.action.disabled,
                }}
              />
              <Chip
                size="small"
                label="Selected"
                sx={{
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  color: theme.palette.primary.main,
                  border: `2px solid ${theme.palette.primary.main}`,
                }}
              />
            </Box>

            {/* Time Slots Grid */}
            <Box sx={{ overflowX: 'auto' }}>
              <Grid container spacing={2}>
                {weekDays.map((day, dayIndex) => (
                  <Grid key={dayIndex} size={{ xs: 12, md: viewMode === 'week' ? Math.min(12/weekDays.length, 1.7) : 12 }}>
                    <Paper
                      sx={{
                        p: 2,
                        backgroundColor: alpha(theme.palette.background.paper, 0.5),
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                        {format(day, 'EEE, MMM d')}
                      </Typography>
                      
                      <Stack spacing={1}>
                        {availableSlots.map((slot, slotIndex) => (
                          <motion.div
                            key={slotIndex}
                            whileHover={{ scale: slot.available ? 1.02 : 1 }}
                            whileTap={{ scale: slot.available ? 0.98 : 1 }}
                          >
                            <Box
                              onClick={() => toggleSlotSelection(slot, day)}
                              sx={{
                                p: 1.5,
                                borderRadius: 1,
                                backgroundColor: getSlotColor(slot, day),
                                border: `2px solid ${alpha(getSlotBorder(slot, day), 0.3)}`,
                                cursor: (() => {
                                  const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
                                  if (isPast(slotDateTime)) return 'not-allowed';
                                  return slot.available ? 'pointer' : 'not-allowed';
                                })(),
                                opacity: (() => {
                                  const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
                                  if (isPast(slotDateTime)) return 0.4;
                                  return slot.available ? 1 : 0.5;
                                })(),
                                transition: 'all 0.2s',
                                '&:hover': slot.available && !isPast(setHours(setMinutes(day, 0), slot.start.getHours())) ? {
                                  borderColor: getSlotBorder(slot, day),
                                  transform: 'translateX(2px)',
                                } : {},
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {getTimeIcon(slot.start.getHours())}
                                  <Typography variant="caption" fontWeight={500}>
                                    {format(slot.start, 'h:mm a')}
                                  </Typography>
                                  {(() => {
                                    const now = new Date();
                                    const slotDateTime = setHours(setMinutes(day, 0), slot.start.getHours());
                                    const nextHour = addHours(now, 1);
                                    
                                    // Show "Now" badge for the current/next available slot
                                    if (isSameDay(slotDateTime, now) && 
                                        slot.start.getHours() === nextHour.getHours() &&
                                        !isPast(slotDateTime)) {
                                      return (
                                        <Chip
                                          label="Next"
                                          size="small"
                                          sx={{
                                            height: 16,
                                            fontSize: '0.65rem',
                                            ml: 0.5,
                                            backgroundColor: theme.palette.info.main,
                                            color: 'white',
                                          }}
                                        />
                                      );
                                    }
                                    return null;
                                  })()}
                                </Box>
                                {selectedTimeSlots.some(s => 
                                  isSameDay(s, day) && s.getHours() === slot.start.getHours()
                                ) && (
                                  <CheckCircle sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                )}
                              </Box>
                              
                              {/* Show time in other zones */}
                              {viewMode === 'day' && formData.customerTimezone && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mt: 0.5 }}>
                                  {getTimeInZone(setHours(day, slot.start.getHours()), formData.customerTimezone)}
                                </Typography>
                              )}
                            </Box>
                          </motion.div>
                        ))}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Proposed Times Summary */}
            {selectedTimeSlots.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Proposed Times ({selectedTimeSlots.length})
                </Typography>
                <Grid container spacing={2}>
                  {selectedTimeSlots.map((slot, index) => (
                    <Grid key={index} size={{ xs: 12, md: 6 }}>
                      <Paper
                        sx={{
                          p: 2,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight={600}>
                              {format(slot, 'EEEE, MMMM d')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(slot, 'h:mm a')} - {format(addHours(slot, formData.duration / 60), 'h:mm a')}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setSelectedTimeSlots(prev => 
                              prev.filter((_, i) => i !== index)
                            )}
                          >
                            <Cancel />
                          </IconButton>
                        </Box>
                        
                        {/* Show in all time zones */}
                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                          {[formData.yourTimezone, formData.customerTimezone, ...formData.additionalTimezones]
                            .filter(Boolean)
                            .map((tz, tzIndex) => {
                              const tzInfo = commonTimezones.find(ctz => ctz.value === tz);
                              if (!tzInfo) return null;
                              
                              return (
                                <Typography key={tzIndex} variant="caption" color="text.secondary">
                                  {tzInfo.label}: {getTimeInZone(slot, tz)}
                                </Typography>
                              );
                            })}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </GlassCard>
        </Grid>

        {/* Meeting Invitation */}
        <Grid size={12}>
          <GlassCard>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
              Send Invitation
            </Typography>

            <Grid container spacing={3}>
              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.sendCalendarInvite}
                      onChange={(e) => setFormData({ ...formData, sendCalendarInvite: e.target.checked })}
                    />
                  }
                  label="Include calendar invite (.ics file)"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  label="Custom Message"
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.customMessage}
                  onChange={(e) => setFormData({ ...formData, customMessage: e.target.value })}
                  placeholder="Add a personal message to the invitation..."
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={sending ? <CircularProgress size={20} /> : <Send />}
                    disabled={!formData.title || selectedTimeSlots.length === 0 || formData.recipients.length === 0 || sending}
                    onClick={handleSendInvitation}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    }}
                  >
                    {sending ? 'Sending...' : 'Send Invitation'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </GlassCard>
        </Grid>
      </Grid>

      {/* Calendar Modal */}
      <Dialog
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Calendar View</Typography>
            <IconButton onClick={() => setCalendarOpen(false)} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <DateCalendar
                value={calendarDate}
                onChange={(newValue) => setCalendarDate(newValue)}
                sx={{
                  maxWidth: 320,
                  '& .MuiPickersDay-root': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiDayCalendar-weekDayLabel': {
                    fontSize: '0.875rem',
                  },
                  '& .MuiDayCalendar-header': {
                    justifyContent: 'space-between',
                    paddingLeft: '12px',
                    paddingRight: '12px',
                  },
                }}
              slots={{
                day: (props: PickersDayProps) => {
                  const dateKey = format(props.day, 'yyyy-MM-dd');
                  const hasMeeting = mockMeetingDates.has(dateKey);
                  return (
                    <Badge
                      key={props.day.toString()}
                      overlap="circular"
                      badgeContent={hasMeeting ? '•' : undefined}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '1.5rem',
                          minWidth: 'auto',
                          height: 'auto',
                          padding: 0,
                          color: theme.palette.primary.main,
                          backgroundColor: 'transparent',
                          top: '75%',
                        },
                      }}
                    >
                      <PickersDay {...props} />
                    </Badge>
                  );
                },
              }}
              />
            </Box>
          </LocalizationProvider>
          
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Upcoming Meetings
            </Typography>
            <Stack spacing={1}>
              <Alert severity="info" icon={<CalendarMonth />}>
                <Typography variant="body2">
                  No meetings scheduled for {calendarDate ? format(calendarDate, 'MMMM d, yyyy') : 'this date'}
                </Typography>
              </Alert>
              <Typography variant="caption" color="text.secondary">
                Integration with Microsoft 365 calendar coming soon
              </Typography>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarOpen(false)}>Close</Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => {
              setCalendarOpen(false);
              // Could navigate to schedule new meeting with selected date
              if (calendarDate) {
                setSelectedDate(calendarDate);
              }
            }}
          >
            Schedule Meeting
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Meetings;